import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./PromptManager.css";
import { useDispatch, useSelector } from "react-redux";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext";
import MasterTable from "../../components/MasterTable/MasterTable";
import SearchBar from "../../components/SearchBar/SearchBar";
import PromptAssistant from "../../components/PromptAssistant/PromptAssistant";
import { fetchPromptManagerCountries, fetchPromptManagerProperties } from "../../store/apiActions";
import { mapApiCountry, mapApiProperty } from "../../utils/helper";

// ─── Assets ─────────────────────────────────────────────────────────────────
import promptIcon from "../../assets/images/prompt-manager-icon.svg";
import promptHover from "../../assets/images/hover-prompt-manager.svg";
import filterIcon from "../../assets/images/filter-icon.svg";
import editIcon from "../../assets/images/edit-prompt-main-icon.svg";


// ─── Feature Flags ───────────────────────────────────────────────────────────
// Set to true to re-enable the Prompt Assistant chatbot panel
const ENABLE_PROMPT_ASSISTANT = false;

// ─── Column Definition ───────────────────────────────────────────────────────

const PROMPT_COLUMNS = [
    {
        label: "Property",
        accessor: "property",
        width: 185,
        minWidth: 175,
        truncate: true,
        maxLen: 20,
    },
    {
        label: "Question",
        accessor: "question",
        width: 230,
        minWidth: 200,
        truncate: true,
        maxLen: 35,
    },
    {
        label: "Instruction",
        accessor: "instruction",
        width: 230,
        minWidth: 200,
        truncate: true,
        maxLen: 35,
    },
    {
        label: "RegOne Object",
        accessor: "regOneObject",
        width: 150,
        minWidth: 130,
        type: "badge",
        align: "center",
        colorMap: {
            Document: "blue",
            Object: "purple",
        },
    },
    {
        label: "Source",
        accessor: "source",
        width: 130,
        minWidth: 110,
        type: "badge",
        align: "center",
        colorMap: {
            Internal: "green",
            External: "orange",
        },
    },
    {
        label: "Technical/Formulation",
        accessor: "technicalFormulation",
        width: 190,
        minWidth: 170,
        type: "badge",
        align: "center",
        colorMap: {
            Formulation: "purple",
            Technical: "red",
        },
    },
    {
        label: "Last Modified",
        accessor: "lastModified",
        width: 150,
        minWidth: 130,
        truncate: false,
    },
    {
        label: "Classification",
        accessor: "classification",
        width: 150,
        minWidth: 130,
        truncate: true,
        maxLen: 12,
    },
];



// ─── CountryCard ─────────────────────────────────────────────────────────────

const CountryCard = ({ country, onClick }) => {
    const isActive = country.status === "Active" || country.status === "Onboarding Complete";

    return (
        <div className="pm-country-card" id={`country-card-${country.id}`}>
            <div className="pm-country-card-header">
                <div className="pm-country-avatar">
                    <span className="pm-country-avatar-text">{country.code}</span>
                </div>
                <div className="pm-country-info">
                    <span className="pm-country-name" title={country.name}>{country.name}</span>
                    <span className="pm-country-meta">
                        {country.prompts} {country.prompts === 1 ? "Prompt" : "Prompts"}
                        {country.onboarded && (
                            <> &middot; {isActive ? "Onboarded" : "Modified"} {country.onboarded}</>
                        )}
                    </span>
                </div>
                <span className={`pm-status-badge pm-status-badge--${isActive ? "active" : "progress"}`}>
                    {country.status}
                </span>
            </div>
            <div className="pm-country-card-footer">
                <button
                    id={`edit-prompts-${country.id}`}
                    className="pm-edit-btn"
                    onClick={() => onClick(country)}
                    type="button"
                >
                    <img src={editIcon} alt="" className="pm-btn-icon" />
                    Edit Prompts
                </button>
            </div>
        </div>
    );
};

const CountryCardSkeleton = () => (
    <div className="pm-country-card pm-country-card--skeleton" aria-hidden="true">
        <div className="pm-country-card-header">
            <div className="pm-country-avatar pm-country-avatar--skeleton pm-skeleton-pulse" />
            <div className="pm-country-info">
                <div className="pm-skeleton-line pm-skeleton-line--title pm-skeleton-pulse" />
                <div className="pm-skeleton-line pm-skeleton-line--sub pm-skeleton-pulse" />
            </div>
            <div className="pm-skeleton-badge pm-skeleton-pulse" />
        </div>
        <div className="pm-country-card-footer">
            <div className="pm-skeleton-btn pm-skeleton-pulse" />
        </div>
    </div>
);

// ─── PromptManager ───────────────────────────────────────────────────────────

const PromptManager = () => {
    const dispatch = useDispatch();
    const { setBreadcrumbs, resetBreadcrumbs } = useBreadcrumb();

    const [view, setView] = useState("list"); // "list" | "table"
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [promptSearch, setPromptSearch] = useState("");
    const [filterOpen, setFilterOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState("all");
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const filterRef = React.useRef(null);

    // Redux state for /pm/countries
    const countriesResponse = useSelector((state) => state?.api?.getPromptManagerCountries);
    const isCountriesLoading = Boolean(countriesResponse?.loading);
    const countriesError = countriesResponse?.error;

    // Redux state for /pm/properties
    const propertiesResponse = useSelector((state) => state?.api?.getPromptManagerProperties);
    const isPropertiesLoading = Boolean(propertiesResponse?.loading);
    const propertiesError = propertiesResponse?.error;

    // Fetch countries from /pm/countries on mount and on debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            const params = searchQuery.trim() ? { search: searchQuery.trim() } : null;
            dispatch(fetchPromptManagerCountries(params));
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, dispatch]);

    // Format countries directly from backend API
    const countries = useMemo(() => {
        const rawData = countriesResponse?.data;
        const list = Array.isArray(rawData)
            ? rawData
            : Array.isArray(rawData?.data)
                ? rawData.data
                : null;

        if (list) {
            return list.map(mapApiCountry);
        }

        return [];
    }, [countriesResponse?.data]);

    // Close filter dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterOpen(false);
            }
        };

        if (filterOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [filterOpen]);

    // Fetch /pm/properties on debounced promptSearch (or activeFilter) change
    useEffect(() => {
        if (!selectedCountry) return;
        const timer = setTimeout(() => {
            const params = { country: selectedCountry.name };
            if (promptSearch.trim()) params.search = promptSearch.trim();
            dispatch(fetchPromptManagerProperties(params));
        }, 300);
        return () => clearTimeout(timer);
    }, [selectedCountry, promptSearch, dispatch]);

    // Map /pm/properties response → table rows
    const promptData = useMemo(() => {
        const rawData = propertiesResponse?.data;
        const list = Array.isArray(rawData)
            ? rawData
            : Array.isArray(rawData?.data)
                ? rawData.data
                : null;
        if (list) return list.map(mapApiProperty);
        return [];
    }, [propertiesResponse?.data]);

    // Client-side filter by activeFilter badge columns (source / technicalFormulation / regOneObject)
    const filteredPrompts = useMemo(() => {
        if (!activeFilter || activeFilter === "all") return promptData;
        const f = activeFilter.toLowerCase();
        return promptData.filter((row) =>
            (row.regOneObject && row.regOneObject.toLowerCase() === f) ||
            (row.source && row.source.toLowerCase() === f) ||
            (row.technicalFormulation && row.technicalFormulation.toLowerCase() === f)
        );
    }, [promptData, activeFilter]);

    // ─── Breadcrumbs ────────────────────────────────────────────────────────

    const goBackToList = useCallback(() => {
        setView("list");
        setSelectedCountry(null);
        setSearchQuery("");
        resetBreadcrumbs();
    }, [resetBreadcrumbs]);

    useEffect(() => {
        if (view === "table" && selectedCountry) {
            setBreadcrumbs([
                {
                    id: "prompt-manager",
                    label: "Prompt Manager",
                    icon: { default: promptIcon, hover: promptHover },
                    onClick: goBackToList,
                },
                {
                    id: "country",
                    label: selectedCountry.name,
                },
            ]);
        } else {
            resetBreadcrumbs();
        }

        return () => {
            resetBreadcrumbs();
        };
    }, [view, selectedCountry, setBreadcrumbs, resetBreadcrumbs, goBackToList]);

    // ─── Handlers ───────────────────────────────────────────────────────────

    const handleCountryClick = (country) => {
        setSelectedCountry(country);
        setView("table");
        setPromptSearch("");
        setSelectedPrompt(null);
        // Fetch fires via useEffect when selectedCountry changes
    };

    const handleEditPrompt = useCallback((row) => {
        setSelectedPrompt(row);
    }, []);

    const handleAssistantClose = () => {
        setSelectedPrompt(null);
    };

    const handleUpdatePrompt = useCallback((promptId, newInstruction) => {
        setSelectedPrompt((prev) =>
            prev && prev.id === promptId
                ? {
                    ...prev,
                    instruction: newInstruction,
                    lastModified: new Date().toISOString().split("T")[0],
                }
                : prev
        );
    }, []);

    // ─── Dynamic columns — inject click handler and edit button on Property cell ──

    const tableColumns = useMemo(() => [
        {
            ...PROMPT_COLUMNS[0],
            width: 185,
            minWidth: 175,
            render: (value, row) => {
                const raw = value != null ? String(value) : "—";
                const isTruncated = raw.length > 20;
                const displayText = isTruncated ? `${raw.slice(0, 20)}...` : raw;

                return (
                    <div className="pm-property-cell">
                        <span
                            className="pm-property-text"
                            title={raw}
                        >
                            {displayText}
                        </span>
                        {ENABLE_PROMPT_ASSISTANT && (
                            <button
                                id={`edit-prompt-${row.id}`}
                                className={`pm-property-edit-btn ${selectedPrompt?.id === row.id ? "pm-property-edit-btn--active" : ""}`}
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditPrompt(row);
                                }}
                                title="Edit prompt"
                                aria-label={`Edit ${raw} prompt`}
                            >
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9448 1.59129C12.1736 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1591 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17514 14.3137 2.38304 14.4084 2.61184C14.5032 2.84064 14.552 3.0858 14.552 3.33337C14.552 3.58095 14.5032 3.8261 14.4084 4.0549C14.3137 4.2837 14.1748 4.4916 13.9997 4.66671L5.33301 13.3334L1.99967 14.3334L2.99967 11.0001L11.333 2.00004Z"
                                        stroke="currentColor"
                                        strokeWidth="1.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>
                );
            },
        },
        ...PROMPT_COLUMNS.slice(1),
    ], [handleEditPrompt, selectedPrompt]);

    // ─── Render: Country List ────────────────────────────────────────────────

    if (view === "list") {
        return (
            <div className="pm-root">
                {/* Search bar */}
                <div className="pm-search-row">
                    <SearchBar
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Search countries..."
                        className="pm-country-searchbar"
                    />
                </div>

                {/* Country cards grid */}
                <div className="pm-cards-grid">
                    {isCountriesLoading ? (
                        Array.from({ length: 6 }).map((_, idx) => (
                            <CountryCardSkeleton key={`skeleton-${idx}`} />
                        ))
                    ) : countriesError && countries.length === 0 ? (
                        <div className="pm-error-state">
                            <span className="pm-error-msg">
                                Failed to load countries. {countriesError}
                            </span>
                            <button
                                type="button"
                                className="pm-retry-btn"
                                onClick={() => {
                                    const params = searchQuery.trim() ? { search: searchQuery.trim() } : null;
                                    dispatch(fetchPromptManagerCountries(params));
                                }}
                            >
                                Try Again
                            </button>
                        </div>
                    ) : countries.length === 0 ? (
                        <div className="pm-empty">No countries found.</div>
                    ) : (
                        countries.map((country) => (
                            <CountryCard
                                key={country?.id}
                                country={country}
                                onClick={handleCountryClick}
                            />
                        ))
                    )}
                </div>
            </div>
        );
    }

    // ─── Render: Prompt Table ──────────────────────────────────────────

    return (
        <div className="pm-table-root">
            {/* Page-level toolbar: Filters button + Search bar — side by side */}
            <div className="pm-table-toolbar">
                <div className="pm-filters-wrapper" ref={filterRef}>
                    <button
                        id="pm-filters-btn"
                        className="pm-filters-btn"
                        onClick={() => setFilterOpen((p) => !p)}
                        type="button"
                    >
                        <img src={filterIcon} alt="" className="pm-btn-icon" />
                        Filters
                    </button>

                    {filterOpen && (
                        <div className="pm-filters-dropdown">
                            {[
                                { label: "All", value: "all" },
                                { label: "Formulation", value: "formulation" },
                                { label: "Technical", value: "technical" },
                                { label: "Internal", value: "internal" },
                                { label: "External", value: "external" },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`pm-filters-option ${activeFilter === opt.value ? "active" : ""}`}
                                    onClick={() => { setActiveFilter(opt.value); setFilterOpen(false); }}
                                    type="button"
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <SearchBar
                    value={promptSearch}
                    onChange={setPromptSearch}
                    placeholder="Search..."
                    className="pm-table-searchbar"
                />
            </div>

            {/* Split pane: table + optional assistant panel */}
            <div className="pm-split-pane">
                <div className="pm-split-table">
                    <MasterTable
                        columns={tableColumns}
                        data={filteredPrompts}
                        loading={isPropertiesLoading}
                        emptyMessage={
                            propertiesError
                                ? `Failed to load prompts. ${propertiesError}`
                                : "No prompts found for this country."
                        }
                        rowKey="id"
                    />
                </div>

                {ENABLE_PROMPT_ASSISTANT && selectedPrompt && (
                    <PromptAssistant
                        prompt={selectedPrompt}
                        onClose={handleAssistantClose}
                        onUpdatePrompt={handleUpdatePrompt}
                    />
                )}
            </div>
        </div>
    );
};

export default PromptManager;
